import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiGift, FiBriefcase, FiUsers, FiCheck } = FiIcons;

const Services = () => {
  const services = [
    {
      icon: FiHeart,
      title: 'Wedding Decorations',
      description: 'Create the wedding of your dreams with our elegant and romantic decoration packages. From ceremony backdrops to reception centerpieces.',
      image: 'https://i.ibb.co/Cp933BqZ/wedding-image.jpg',
      features: ['Bridal arch decorations', 'Table centerpieces', 'Lighting setup', 'Floral arrangements']
    },
    {
      icon: FiGift,
      title: 'Birthday Parties',
      description: 'Make every birthday celebration special with our fun and colorful decoration themes suitable for all ages.',
      image: 'https://i.ibb.co/dJ0gX1bJ/birthday-image.jpg',
      features: ['Balloon arrangements', 'Theme decorations', 'Backdrop setup', 'Party favors']
    },
    {
      icon: FiBriefcase,
      title: 'Corporate Events',
      description: 'Professional and sophisticated decorations for business events, conferences, and corporate celebrations.',
      image: 'https://i.ibb.co/7Js0yrrg/corporate-image.jpg',
      features: ['Professional setup', 'Brand integration', 'Stage decorations', 'Networking areas']
    },
    {
      icon: FiUsers,
      title: 'Anniversary Celebrations',
      description: 'Celebrate milestones with beautiful decorations that reflect your journey together.',
      image: 'https://i.ibb.co/PvQk4vck/anniversary-image.webp',
      features: ['Romantic themes', 'Photo displays', 'Memory lanes', 'Elegant centerpieces']
    }
  ];

  const packages = [
    {
      name: 'Basic Package',
      price: 'Starting at $299',
      description: 'Perfect for small intimate gatherings',
      features: [
        'Basic decoration setup',
        'Color-coordinated theme',
        'Table centerpieces',
        '2-hour setup service',
        'Cleanup included'
      ]
    },
    {
      name: 'Premium Package',
      price: 'Starting at $599',
      description: 'Ideal for medium-sized celebrations',
      features: [
        'Complete decoration setup',
        'Custom theme design',
        'Backdrop and photo area',
        'Lighting arrangements',
        '4-hour setup service',
        'Cleanup included',
        'Photography props'
      ]
    },
    {
      name: 'Luxury Package',
      price: 'Starting at $999',
      description: 'Ultimate experience for grand events',
      features: [
        'Full-scale decoration design',
        'Premium materials',
        'Multiple themed areas',
        'Professional lighting',
        'Floral arrangements',
        'Full-day service',
        'Cleanup included',
        'Event coordination'
      ]
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-800 to-red-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Services</h1>
            <p className="text-xl text-red-100 max-w-3xl mx-auto">
              Professional event decoration services tailored to make your celebrations memorable. 
              From intimate gatherings to grand events, we create the perfect atmosphere.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col"
              >
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-64 object-cover"
                />
                <div className="p-6 flex-grow flex flex-col">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={service.icon} className="w-5 h-5 text-red-800" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800">{service.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-4 flex-grow">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2 text-sm text-gray-600">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="bg-red-800 text-white px-4 py-2 rounded-lg hover:bg-red-900 transition-colors duration-200 inline-block text-center mt-auto"
                  >
                    Get Quote
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Our Packages
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Choose from our carefully crafted packages or let us create a custom solution 
              that perfectly fits your event needs and budget.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`bg-white rounded-lg shadow-lg p-8 ${
                  index === 1 ? 'ring-2 ring-red-800 transform scale-105' : ''
                }`}
              >
                {index === 1 && (
                  <div className="bg-red-800 text-white text-center py-2 px-4 rounded-full text-sm font-semibold mb-6">
                    Most Popular
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{pkg.name}</h3>
                  <p className="text-3xl font-bold text-red-800 mb-2">{pkg.price}</p>
                  <p className="text-gray-600">{pkg.description}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`block text-center py-3 px-6 rounded-lg font-semibold transition-colors duration-200 ${
                    index === 1
                      ? 'bg-red-800 text-white hover:bg-red-900'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  Choose Package
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Our Process
            </h2>
            <p className="text-gray-600 text-lg">
              Simple steps to bring your event vision to life
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Consultation', description: 'Discuss your vision and requirements' },
              { step: '2', title: 'Design', description: 'Create custom decoration plan' },
              { step: '3', title: 'Setup', description: 'Professional installation on event day' },
              { step: '4', title: 'Cleanup', description: 'Complete cleanup after your event' }
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-red-800 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-red-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Plan Your Event?
            </h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Contact us today for a free consultation and let's discuss how we can make 
              your event absolutely spectacular.
            </p>
            <Link
              to="/contact"
              className="bg-yellow-400 text-red-900 px-8 py-4 rounded-lg font-semibold hover:bg-yellow-300 transition-colors duration-200 inline-block"
            >
              Get Free Consultation
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;